/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
/* global globalThis */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import Filters from "./../../../../assets/images/filters.svg";
import Threedots from "./../../../../assets/images/PhosphorLine.svg";
import Refresh from "./../../../../assets/images/ArrowCounterClockwise.svg";

import OverviewLossRecovery from "./overviewLossRecovery/OverviewLossRecovery";
import styles from "./lossRecoveryEngine.module.css";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import MoreFilters from "Samarth Module/components/drawers/moreFilters";
import CustomiseColumns from "Samarth Module/components/drawers/CustomiseColumns";
import BGLevelSummary from "./summaryLevel/BGLevelSummary";
import ChannelLevelSummary from "./summaryLevel/ChannelLevelSummary";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";

import {
  fetchLossRecoveryFilters,
  fetchLossRecoveryKPIs,
  fetchLossRecoveryOverView,
  fetchBGLevelSummary,
  fetchChannelLevelSummary,
  exportLossRecoveryOverview,
} from "../../../api/LossRecoveryApi";
import CustomCalendar from "./utils/CustomCalendar";
import LossRecoveryKPI from "./lossRecoveryKPI/LossRecoveryKPI";

import { Box, Chip, CircularProgress } from "@mui/material";

// lightweight shimmer
export const Shimmer = ({ w = "100%", h = 24, br = 10 }) => (
  <div
    className={styles.shimmerBase}
    style={{
      width: w,
      height: typeof h === "number" ? `${h}px` : h,
      borderRadius: br,
    }}
  />
);

Shimmer.propTypes = {
  w: PropTypes.string,
  h: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  br: PropTypes.number,
};

Shimmer.propTypes = {
  w: PropTypes.string,
  h: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  br: PropTypes.number,
};

const mergeSelectedFilterData = (
  incomingFilters = [],
  existingFilters = [],
) => {
  return incomingFilters.map((newFilter) => {
    const existingFilter = existingFilters.find(
      (f) => f.label === newFilter.label,
    );

    return {
      ...newFilter,
      selectedData: existingFilter?.selectedData || [],
    };
  });
};

const buildFilterChips = (filterData, handleDelete, defaultFilterData = []) => {
  const chips = [];

  const effectiveFilterData = filterData || defaultFilterData || [];

  effectiveFilterData.forEach((filter, index) => {
    if (
      filter.isFavourite ||
      filter.isFavorite ||
      !filter.selectedData?.length
    ) {
      return;
    }

    filter.selectedData.forEach((selectedValue, subIndex) => {
      chips.push({
        key: `${filter.label}-${index}-${subIndex}`,
        label: selectedValue,
        onDelete: () => handleDelete(filter.label, subIndex),
      });
    });
  });

  return chips;
};

const isAbortError = (error) =>
  error?.name === "AbortError" ||
  error?.message?.toLowerCase?.().includes("aborted");

const LossRecoveryEngine = ({ isActive = true }) => {
  const { accounts } = useMsal();
  const activeAccount = accounts[0];

  // UOM options
  const uomOptions = ["Cases", "Tonnes", "Eaches"];

  const [selectedAction, setSelectedAction] = useState([]);
  const [selectedView, setSelectedView] = useState("Order Loss");
  const [sortState, setSortState] = useState({
    sortBy: "",
    sortOrder: "",
    fieldsection: "",
  });

  const [openCalendar, setOpenCalendar] = useState(false);
  const [calendarValue, setCalendarValue] = useState({
    start: null,
    end: null,
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState({
    filterDrawer: false,
    customiseDrawer: false,
  });

  const [filters, setFilters] = useState({
    lossRecoveryFiltersData: [],
    defaultColsOverviewTable: [],
  });

  const [lossRecoveryData, setLossRecoveryData] = useState({
    kpiData: [],
    // lossRecoveryOrderExpiryData: [],
    bgSummaryLevelData: [],
    channelSummaryLevelData: [],
    overviewTableData: [],
  });

  const [loading, setLoading] = useState({
    kpiLoading: false,
    orderAllocationLoading: false,
    allocationExpiringLoading: false,
    summaryBGLevelLoading: false,
    summaryChannelLevelLoading: false,
    initialLoading: true,
    overviewTableLoading: false,
  });

  const [exportLoading, setExportLoading] = useState(false);
  const [summaryLevel, setSummaryLevel] = useState("");
  const [levelOptions, setLevelOptions] = useState([]);
  const [uom, setUom] = useState("Tonnes");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("error");
  const orderExpiryLevel = "Overall";
  const orderExpiryStyle = "Chart";

  // request guards to prevent stale responses from overwriting latest UI
  const dashboardRequestRef = useRef(0);
  const filterMetaRequestRef = useRef(0);
  const overviewRequestRef = useRef(0);
  const kpiRequestRef = useRef(0);

  // AbortController to cancel in-flight requests
  const abortControllerRef = useRef(new AbortController());

  const buildFiltersMap = useCallback((filterList) => {
    const list = filterList || [];
    return list.reduce((acc, filter) => {
      acc[filter.label] = filter.selectedData;
      return acc;
    }, {});
  }, []);

  const showErrorSnackbar = useCallback((error) => {
    const message =
      error?.message ||
      "An error occurred while fetching data. Please try again.";
    setSnackbarMessage(message);
    setSnackbarType("error");
    setSnackbarOpen(true);
  }, []);

  /**
   * INITIAL LOAD ONLY
   * - fetch filter metadata first
   * - then trigger all dashboard APIs in parallel
   */
  const fetchInitialData = useCallback(
    async (suggestedOrderFilters, uomOverride) => {
      const filters = suggestedOrderFilters || [];
      const currentUom = uomOverride ?? uom;
      try {
        const payload = {
          email: activeAccount?.username,
          uom: currentUom,
          filters: buildFiltersMap(filters),
          type: "Loss Recovery Engine",
          lossMitigationToggle: selectedView,
          level: orderExpiryLevel,
          style: orderExpiryStyle,
        };

        const signal = abortControllerRef.current?.signal;
        const lossRecoveryFiltersData = await fetchLossRecoveryFilters(
          payload,
          signal,
        );

        setFilters((prev) => ({
          ...prev,
          lossRecoveryFiltersData: lossRecoveryFiltersData?.filters || [],
          defaultColsOverviewTable:
            lossRecoveryFiltersData?.defaultColumns || [],
        }));

        // return filters for caller to use (moved to useEffect for ordering)
        return lossRecoveryFiltersData?.filters || filters;
      } catch (error) {
        if (!isAbortError(error)) {
          console.error("Error fetching initial filter data:", error);
          showErrorSnackbar(error);
        }
        return filters;
      } finally {
        setLoading((prev) => ({ ...prev, initialLoading: false }));
      }
    },
    [
      activeAccount?.username,
      uom,
      selectedView,
      orderExpiryLevel,
      orderExpiryStyle,
      buildFiltersMap,
      showErrorSnackbar,
    ],
  );

  /**
   * FILTER METADATA ONLY
   * Used in background so UI isn't blocked.
   * IMPORTANT: preserve selectedData to avoid dropdown remount/reset effects.
   */
  const refreshFilterMeta = useCallback(
    async (suggestedOrderFilters, uomOverride) => {
      try {
        const requestId = ++filterMetaRequestRef.current;

        const payload = {
          email: activeAccount?.username,
          uom: uomOverride ?? uom,
          filters: buildFiltersMap(suggestedOrderFilters),
          type: "Loss Recovery Engine",
          lossMitigationToggle: selectedView,
          level: orderExpiryLevel,
          style: orderExpiryStyle,
        };

        const signal = abortControllerRef.current?.signal;
        const lossRecoveryFiltersData = await fetchLossRecoveryFilters(
          payload,
          signal,
        );

        // ignore stale response
        if (requestId !== filterMetaRequestRef.current) return;

        setFilters((prev) => ({
          ...prev,
          lossRecoveryFiltersData:
            mergeSelectedFilterData(
              lossRecoveryFiltersData?.filters || [],
              prev.lossRecoveryFiltersData,
            ) || prev.lossRecoveryFiltersData,
          defaultColsOverviewTable:
            lossRecoveryFiltersData?.defaultColumns ||
            prev.defaultColsOverviewTable,
        }));
      } catch (error) {
        if (!isAbortError(error)) {
          console.error("Error refreshing filter data:", error);
          showErrorSnackbar(error);
        }
      }
    },
    [
      activeAccount?.username,
      uom,
      selectedView,
      orderExpiryLevel,
      orderExpiryStyle,
      buildFiltersMap,
      showErrorSnackbar,
    ],
  );

  const isOverviewReady =
    !loading["overviewTableLoading"] &&
    lossRecoveryData?.overviewTableData &&
    Object.keys(lossRecoveryData.overviewTableData).length > 0;

  /**
   * MAIN DASHBOARD FETCH
   * All APIs are triggered in parallel immediately.
   */
  const fetchData = useCallback(
    async (suggestedOrderFilters, uomOverride) => {
      const currentRequestId = ++dashboardRequestRef.current;

      const payload = {
        email: activeAccount?.username,
        uom: uomOverride ?? uom,
        filters: buildFiltersMap(suggestedOrderFilters),
        type: "Loss Recovery Engine",
        expand: false,
        lossMitigationToggle: selectedView,
        level: orderExpiryLevel,
        style: orderExpiryStyle,
      };

      const channelPayload = summaryLevel
        ? { ...payload, value_level: summaryLevel }
        : payload;

      // set all loaders true
      setLoading((prev) => ({
        ...prev,
        kpiLoading: true,
        orderAllocationLoading: true,
        allocationExpiringLoading: true,
        summaryBGLevelLoading: true,
        summaryChannelLevelLoading: true,
        overviewTableLoading: true,
      }));

      // Clear overview table data to prevent stale data display
      setLossRecoveryData((prev) => ({
        ...prev,
        overviewTableData: {},
      }));

      // Get abort signal for this request cycle
      const signal = abortControllerRef.current?.signal;

      // KPI API
      fetchLossRecoveryKPIs(payload, signal)
        .then((res) => {
          if (currentRequestId !== dashboardRequestRef.current) return;
          setLossRecoveryData((prev) => ({
            ...prev,
            kpiData: res?.kpis || [],
          }));
        })
        .catch((err) => {
          if (!isAbortError(err)) {
            console.error("KPI API error:", err);
            showErrorSnackbar(err);
          }
        })
        .finally(() => {
          if (currentRequestId !== dashboardRequestRef.current) return;
          setLoading((prev) => ({ ...prev, kpiLoading: false }));
        });

      // BG Summary
      fetchBGLevelSummary(payload, signal)
        .then((res) => {
          if (currentRequestId !== dashboardRequestRef.current) return;
          setLossRecoveryData((prev) => ({
            ...prev,
            bgSummaryLevelData: res || [],
          }));
        })
        .catch((err) => {
          if (!isAbortError(err)) {
            console.error("BG Summary error:", err);
            showErrorSnackbar(err);
          }
        })
        .finally(() => {
          if (currentRequestId !== dashboardRequestRef.current) return;
          setLoading((prev) => ({ ...prev, summaryBGLevelLoading: false }));
        });

      // Allocation Expiry
      // fetchLossRecoveryOrderExpiry(payload)
      //   .then((res) => {
      //     if (currentRequestId !== dashboardRequestRef.current) return;
      //     setLossRecoveryData((prev) => ({
      //       ...prev,
      //       lossRecoveryOrderExpiryData: res || {},
      //     }));
      //   })
      //   .catch((err) => console.error("Expiry API error:", err))
      //   .finally(() => {
      //     if (currentRequestId !== dashboardRequestRef.current) return;
      //     setLoading((prev) => ({ ...prev, allocationExpiringLoading: false }));
      //   });

      // Channel Summary
      fetchChannelLevelSummary(channelPayload, signal)
        .then((res) => {
          if (currentRequestId !== dashboardRequestRef.current) return;

          setLossRecoveryData((prev) => ({
            ...prev,
            channelSummaryLevelData: res || {},
          }));

          const lf = res?.level_filter;
          if (lf) {
            if (typeof lf.selected_level === "string") {
              setSummaryLevel(lf.selected_level);
            }
            if (Array.isArray(lf.level_filter_values)) {
              setLevelOptions(lf.level_filter_values);
            }
          }
        })
        .catch((err) => {
          if (!isAbortError(err)) {
            console.error("Channel Summary error:", err);
            showErrorSnackbar(err);
          }
        })
        .finally(() => {
          if (currentRequestId !== dashboardRequestRef.current) return;
          setLoading((prev) => ({
            ...prev,
            summaryChannelLevelLoading: false,
          }));
        });

      // Overview Table
      fetchLossRecoveryOverView(payload, signal)
        .then((res) => {
          if (currentRequestId !== dashboardRequestRef.current) {
            return;
          }
          setLossRecoveryData((prev) => ({
            ...prev,
            overviewTableData: res || {},
          }));
        })
        .catch((err) => {
          if (!isAbortError(err)) {
            console.error("Overview API error:", err);
            showErrorSnackbar(err);
          }
        })
        .finally(() => {
          if (currentRequestId !== dashboardRequestRef.current) return;
          setLoading((prev) => ({ ...prev, overviewTableLoading: false }));
        });

      // Order Allocation is static for now, so no API wait required
      setLoading((prev) => ({ ...prev, orderAllocationLoading: false }));
    },
    [
      activeAccount?.username,
      uom,
      selectedView,
      orderExpiryLevel,
      orderExpiryStyle,
      summaryLevel,
      buildFiltersMap,
      showErrorSnackbar,
    ],
  );

  /**
   * KPI-only fetch
   */
  const fetchKPIsOnly = async (
    suggestedOrderFilters,
    uomOverride,
    opts = {},
  ) => {
    const requestId = ++kpiRequestRef.current;
    const suppressLoader = opts?.suppressLoader === true;

    if (!suppressLoader) {
      setLoading((prev) => ({ ...prev, kpiLoading: true }));
    }

    try {
      const payload = {
        email: activeAccount?.username,
        uom: uomOverride ?? uom,
        filters: buildFiltersMap(suggestedOrderFilters || []),
        type: "Loss Recovery Engine",
        lossMitigationToggle: opts?.lossMitigationToggle ?? selectedView,
      };

      const signal = abortControllerRef.current?.signal;
      const kpiRes = await fetchLossRecoveryKPIs(payload, signal);

      if (requestId !== kpiRequestRef.current) return;

      setLossRecoveryData((prev) => ({
        ...prev,
        kpiData: kpiRes?.kpis || [],
      }));
    } catch (e) {
      if (!isAbortError(e)) {
        console.error("Error fetching KPIs only:", e);
        showErrorSnackbar(e);
      }
    } finally {
      if (!suppressLoader && requestId === kpiRequestRef.current) {
        setLoading((prev) => ({ ...prev, kpiLoading: false }));
      }
    }
  };

  const handleActionSuggestedInOverview = (values) => {
    const raw = Array.isArray(values) ? values : [];
    const normalized = raw.filter((v) => v !== "All");

    const finalAction =
      raw.length ===
      (lossRecoveryData["overviewTableData"]?.["Action Suggested"]
        ?.action_filters_data?.length || 0)
        ? []
        : normalized;

    setSelectedAction(finalAction);

    fetchOverviewOnly(filters["lossRecoveryFiltersData"] || [], finalAction, {
      ...sortState,
      page: lossRecoveryData?.overviewTableData?.pagination?.page || 1,
      pageSize: lossRecoveryData?.overviewTableData?.pagination?.pageSize || 10,
    }).catch((error) => {
      if (!isAbortError(error)) {
        console.error("Error fetching overview:", error);
      }
    });
  };

  const handleViewSelection = (val) => {
    setSelectedView(val);

    const currentFilterList = filters["lossRecoveryFiltersData"] || [];
    const serverPage =
      lossRecoveryData?.overviewTableData?.pagination?.page || 1;
    const serverPageSize =
      lossRecoveryData?.overviewTableData?.pagination?.pageSize || 10;

    // keep existing behavior: fetch only KPI + Overview for selected view
    fetchKPIsOnly(currentFilterList, undefined, {
      lossMitigationToggle: val,
    }).catch((error) => {
      if (!isAbortError(error)) {
        console.error("Error fetching KPIs:", error);
        showErrorSnackbar(error);
      }
    });
    fetchOverviewOnly(
      currentFilterList,
      selectedAction,
      { ...sortState, page: serverPage, pageSize: serverPageSize },
      { lossMitigationToggle: val },
    ).catch((error) => {
      if (!isAbortError(error)) {
        console.error("Error fetching overview:", error);
        showErrorSnackbar(error);
      }
    });
  };

  const fetchOverviewOnly = async (
    suggestedOrderFilters = [],
    selectedActionArr = [],
    sort = {},
    opts = {},
  ) => {
    const requestId = ++overviewRequestRef.current;
    const suppressLoader = opts?.suppressLoader === true;

    if (!suppressLoader) {
      setLoading((prev) => ({ ...prev, overviewTableLoading: true }));
    }

    // CRITICAL FIX: Clear old overview data IMMEDIATELY to prevent stale rows from accumulating
    // This ensures that if a newer request's response arrives, old rows won't remain in the UI
    setLossRecoveryData((prev) => ({
      ...prev,
      overviewTableData: {},
    }));

    try {
      const serverPage =
        lossRecoveryData?.overviewTableData?.pagination?.page || 1;
      const serverPageSize =
        lossRecoveryData?.overviewTableData?.pagination?.pageSize || 10;

      const {
        sortBy = "",
        sortOrder = "",
        fieldsection = "",
        page = serverPage,
        pageSize = serverPageSize,
      } = sort || {};

      const payload = {
        email: activeAccount?.username,
        uom,
        filters: buildFiltersMap(suggestedOrderFilters || []),
        type: "Loss Recovery Engine",
        expand: false,
        action: Array.isArray(selectedActionArr) ? selectedActionArr : [],
        sortBy,
        sortOrder,
        fieldsection,
        page,
        pageSize,
        lossMitigationToggle: opts?.lossMitigationToggle ?? selectedView,
      };

      const signal = abortControllerRef.current?.signal;
      const overviewTableData = await fetchLossRecoveryOverView(
        payload,
        signal,
      );

      if (requestId !== overviewRequestRef.current) return;

      setLossRecoveryData((prev) => ({ ...prev, overviewTableData }));
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("Error fetching overview only:", error);
        showErrorSnackbar(error);
      }
    } finally {
      if (!suppressLoader && requestId === overviewRequestRef.current) {
        setLoading((prev) => ({ ...prev, overviewTableLoading: false }));
      }
    }
  };

  const handleSortChange = ({ sortBy, fieldsection }) => {
    const sameColumn =
      sortState.sortBy === sortBy &&
      (sortState.fieldsection || "") === (fieldsection || "");

    let nextOrder = "DESC";
    if (sameColumn) {
      nextOrder = sortState.sortOrder === "ASC" ? "DESC" : "ASC";
    }

    const clean = {
      sortBy,
      sortOrder: nextOrder,
      fieldsection: fieldsection || "",
    };

    setSortState(clean);

    // FIX: Reset page to 1 when sort changes, don't carry over previous page
    // This ensures consistent pagination for the newly sorted results
    const pageSize =
      lossRecoveryData?.overviewTableData?.pagination?.pageSize || 10;

    fetchOverviewOnly(
      filters["lossRecoveryFiltersData"] || [],
      selectedAction,
      { ...clean, page: 1, pageSize },
    ).catch((error) => {
      if (!isAbortError(error)) {
        console.error("Error fetching overview:", error);
      }
    });
  };

  const toggleFilterDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => ({ ...prev, filterDrawer: !prev.filterDrawer }));
  }, []);

  const handlePageChange = (newPage) => {
    const page = Math.max(1, Number(newPage) || 1);
    const pageSize =
      lossRecoveryData?.overviewTableData?.pagination?.pageSize || 10;

    fetchOverviewOnly(
      filters["lossRecoveryFiltersData"] || [],
      selectedAction,
      { ...sortState, page, pageSize },
      { suppressLoader: false },
    ).catch((error) => {
      if (!isAbortError(error)) {
        console.error("Error fetching overview:", error);
        showErrorSnackbar(error);
      }
    });
  };

  /**
   * UPDATED:
   * On filter change:
   * 1. update UI state immediately
   * 2. trigger all main APIs in parallel immediately
   * 3. refresh filter metadata to fetch updated filter options
   */
  const handleFilterChange = useCallback(
    (label, selectedOptions) => {
      const updatedFilters = (filters["lossRecoveryFiltersData"] || []).map(
        (filter) =>
          filter.label === label
            ? { ...filter, selectedData: selectedOptions }
            : filter,
      );

      setFilters((prevFilters) => ({
        ...prevFilters,
        lossRecoveryFiltersData: updatedFilters,
      }));

      // Trigger all APIs immediately
      fetchData(updatedFilters).catch((error) => {
        if (!isAbortError(error)) {
          console.error("Error fetching data:", error);
          showErrorSnackbar(error);
        }
      });

      // Call fetchLossRecoveryFilters API to refresh filter metadata
      refreshFilterMeta(updatedFilters).catch((error) => {
        if (!isAbortError(error)) {
          console.error("Error refreshing filter meta:", error);
          showErrorSnackbar(error);
        }
      });
    },
    [filters, fetchData, refreshFilterMeta],
  );

  const handleChipDelete = useCallback(
    (label, subIndex) => {
      const currentFilter = (filters["lossRecoveryFiltersData"] || []).find(
        (filter) => filter.label === label,
      );

      const updatedSelectedData = (currentFilter?.selectedData || []).filter(
        (_, i) => i !== subIndex,
      );

      handleFilterChange(label, updatedSelectedData);
    },
    [filters, handleFilterChange],
  );

  // Chips for non-favourite (More Filters) selections
  const filterChips = useMemo(
    () =>
      buildFilterChips(
        filters["lossRecoveryFiltersData"],
        handleChipDelete,
        [],
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, handleChipDelete],
  );

  /**
   * Reset should trigger main APIs immediately
   */
  const resetFilter = () => {
    const clearedFilters = (filters["lossRecoveryFiltersData"] || []).map(
      (filter) => ({
        ...filter,
        selectedData: [],
      }),
    );

    setFilters((prevFilters) => ({
      ...prevFilters,
      lossRecoveryFiltersData: clearedFilters,
    }));

    fetchData(clearedFilters).catch((error) => {
      if (!isAbortError(error)) {
        console.error("Error fetching data:", error);
      }
    });
    refreshFilterMeta(clearedFilters).catch((error) => {
      if (!isAbortError(error)) {
        console.error("Error refreshing filter meta:", error);
      }
    });
  };

  const currentFiltersMap = useMemo(() => {
    return (filters["lossRecoveryFiltersData"] || []).reduce((acc, f) => {
      acc[f.label] = f.selectedData || [];
      return acc;
    }, {});
  }, [filters]);

  // Allocation Expiry API call commented out - functionality removed
  //   [activeAccount?.username, uom, currentFiltersMap]
  // );

  const toggleCustomizeDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => ({
      ...prev,
      customiseDrawer: !prev.customiseDrawer,
    }));
  }, []);

  const handleRowsPerPageChange = (newPageSize) => {
    const size = Math.max(1, Number(newPageSize) || 10);

    fetchOverviewOnly(
      filters["lossRecoveryFiltersData"] || [],
      selectedAction,
      { ...sortState, page: 1, pageSize: size },
      { suppressLoader: false },
    ).catch((error) => {
      if (!isAbortError(error)) {
        console.error("Error fetching overview:", error);
      }
    });
  };

  const handleOverviewExport = useCallback(async () => {
    const fallbackSortData =
      lossRecoveryData?.overviewTableData?.SortData || {};

    const payload = {
      email: activeAccount?.username,
      uom,
      type: "Loss Recovery Engine",
      filters: buildFiltersMap(filters["lossRecoveryFiltersData"] || []),
      action: selectedAction,
      sortBy: sortState?.sortBy || fallbackSortData?.sortBy || "",
      sortOrder: sortState?.sortOrder || fallbackSortData?.sortOrder || "",
      fieldsection:
        sortState?.fieldsection || fallbackSortData?.fieldsection || "",
      page: lossRecoveryData?.overviewTableData?.pagination?.page || 1,
      pageSize: lossRecoveryData?.overviewTableData?.pagination?.pageSize || 10,
      lossMitigationToggle: selectedView,
    };

    console.log("[LRE Export] Overview Excel click", payload);
    setExportLoading(true);

    try {
      await exportLossRecoveryOverview(payload);
      console.log("[LRE Overview Export] success");
    } catch (err) {
      console.error("[LRE Overview Export] error", err);
    } finally {
      setExportLoading(false);
    }
  }, [
    activeAccount?.username,
    filters,
    selectedAction,
    sortState,
    lossRecoveryData?.overviewTableData?.SortData,
    lossRecoveryData?.overviewTableData?.pagination,
    uom,
    selectedView,
    buildFiltersMap,
  ]);

  const handleSummaryLevelChange = async (nextLevel) => {
    setSummaryLevel(nextLevel);
    setLoading((prev) => ({ ...prev, summaryChannelLevelLoading: true }));

    try {
      const payload = {
        email: activeAccount?.username,
        uom,
        filters: buildFiltersMap(filters["lossRecoveryFiltersData"] || []),
        type: "Loss Recovery Engine",
        value_level: nextLevel,
        expand: false,
        lossMitigationToggle: selectedView,
      };

      const resp = await fetchChannelLevelSummary(payload);

      setLossRecoveryData((prev) => ({
        ...prev,
        channelSummaryLevelData: resp,
      }));

      const lf = resp?.level_filter;
      if (lf) {
        if (typeof lf.selected_level === "string") {
          setSummaryLevel(lf.selected_level);
        }
        if (Array.isArray(lf.level_filter_values)) {
          setLevelOptions(lf.level_filter_values);
        }
      }
    } catch (e) {
      console.error("Error fetching channel summary by level:", e);
    } finally {
      setLoading((prev) => ({ ...prev, summaryChannelLevelLoading: false }));
    }
  };

  /**
   * UOM change should also trigger all APIs immediately
   * and refresh filter metadata in background
   */
  const handleUoMChange = (label, selectedOptions = "Tonnes") => {
    setUom(selectedOptions);

    fetchData(filters["lossRecoveryFiltersData"] || [], selectedOptions).catch(
      (error) => {
        if (!isAbortError(error)) {
          console.error("Error fetching data:", error);
        }
      },
    );
    refreshFilterMeta(
      filters["lossRecoveryFiltersData"] || [],
      selectedOptions,
    ).catch((error) => {
      if (!isAbortError(error)) {
        console.error("Error refreshing filter meta:", error);
      }
    });
  };

  useEffect(() => {
    const isOrderToDeliveryHub =
      typeof globalThis !== "undefined" &&
      globalThis.location.pathname === "/samarth/order_to_delivery_hub";

    // If component becomes inactive, abort all in-flight requests
    if (!isActive) {
      abortControllerRef.current.abort();
      // Create new abort controller for future requests
      abortControllerRef.current = new AbortController();
      return;
    }

    // Only fetch data if component is active and on the correct route
    if (activeAccount?.username && isOrderToDeliveryHub && isActive) {
      // Create new abort controller for this fetch cycle
      abortControllerRef.current = new AbortController();

      // Call fetchInitialData and then fetchData in sequence
      void (async () => {
        try {
          const initialFilters = await fetchInitialData();
          // fetchData is now available and will be called with returned filters
          await fetchData(initialFilters).catch((error) => {
            if (!isAbortError(error)) {
              console.error("Error fetching data:", error);
            }
          });
        } catch (error) {
          if (!isAbortError(error)) {
            console.error("Error in initial data fetch:", error);
          }
        }
      })();
    }

    // Cleanup: abort requests when component unmounts or becomes inactive
    return () => {
      abortControllerRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccount?.username, isActive]);

  return loading["initialLoading"] ? (
    <div className={styles.loader}>
      <CircularProgress />
    </div>
  ) : (
    <div
      style={{ overflowY: isOverviewReady ? "auto" : "hidden" }}
      className={styles.lrMainContainer}
    >
      {/* Fixed Header Section: KPI, Filters, Chips */}
      <div className={styles.fixedHeaderSection}>
        {/* KPI - Always first so layout does not jump */}
        <div className={styles.kpi_container}>
          {loading["kpiLoading"] ? (
            <div className={styles.loaderContainer}>
              <Shimmer w="100%" h={90} br={8} />
            </div>
          ) : (
            <div className={styles.kpiSectionContainer}>
              <LossRecoveryKPI data={lossRecoveryData?.kpiData} />
            </div>
          )}
        </div>

        {/* Filters - Always below KPI */}
        <div className={styles.sectionContainer}>
          {filters["lossRecoveryFiltersData"]
            .filter((filter) => filter.isFavourite)
            .map((filter) => (
              <div key={filter.label} className={styles.filterWrapper}>
                <MultiSelectWithSearchInput
                  key={filter.label}
                  label={filter.label}
                  options={filter.data}
                  filterValues={filter.selectedData}
                  isMultiSelect={filter.isMultiSelect}
                  handleFilter={handleFilterChange}
                  type="Order To Delivery Hub"
                  defaultValue={filter.defaultValue || []}
                />
              </div>
            ))}

          {/* UOM Filter */}
          <div className={styles.filterWrapper}>
            <MultiSelectWithSearchInput
              label="Units"
              options={uomOptions}
              filterValues={[uom]}
              isMultiSelect={false}
              handleFilter={handleUoMChange}
              type="Order To Delivery Hub"
              defaultValue="Tonnes"
            />
          </div>

          <div className={styles.iconContainer}>
            <button
              type="button"
              onClick={resetFilter}
              className={styles.iconStyling}
              title="Refresh filters"
              aria-label="Refresh filters"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <img src={Refresh} alt="refresh filter" />
            </button>

            <OverflowMenu
              menuItems={[
                {
                  label: "More Filters",
                  icon: Filters,
                  onClick: toggleFilterDrawer,
                },
              ]}
              Icon={Threedots}
            />
          </div>
        </div>

        {/* Filter Chips - Only rendered when present */}
        {filterChips.length > 0 && (
          <div className={styles.filtersBtnContainer}>
            <Box className={styles.chipContainer}>
              {filterChips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  onDelete={chip.onDelete}
                />
              ))}
            </Box>
          </div>
        )}
      </div>

      {/* Scrollable Content Section: BG Summary, Channel Summary, Overview */}
      <div className={styles.scrollableContentSection}>
        <div className={styles.sectionContainer}>
          {loading["summaryBGLevelLoading"] ? (
            <div className={styles.loaderContainer}>
              <Shimmer w="100%" h={320} br={8} />
            </div>
          ) : (
            <BGLevelSummary
              fetchBGLevelSummary={fetchBGLevelSummary}
              activeAccount={activeAccount}
              uom={uom}
              currentFiltersMap={currentFiltersMap}
              apiType={"Loss Recovery Engine"}
              payload={lossRecoveryData?.["bgSummaryLevelData"]}
            />
          )}

          {loading["summaryChannelLevelLoading"] ? (
            <div className={styles.loaderContainer}>
              <Shimmer w="100%" h={320} br={8} />
            </div>
          ) : (
            <ChannelLevelSummary
              summaryLevel={summaryLevel}
              levelOptions={levelOptions}
              onLevelChange={handleSummaryLevelChange}
              fetchChannelLevelSummary={fetchChannelLevelSummary}
              activeAccount={activeAccount}
              uom={uom}
              currentFiltersMap={currentFiltersMap}
              apiType={"Loss Recovery Engine"}
              payload={lossRecoveryData?.["channelSummaryLevelData"]}
              loading={loading["summaryChannelLevelLoading"]}
              ShimmerComp={Shimmer}
            />
          )}
        </div>

        <div className={styles.olrOuter}>
          <OverviewLossRecovery
            overview={lossRecoveryData?.["overviewTableData"]}
            onActionSuggestedChange={handleActionSuggestedInOverview}
            onSortChange={handleSortChange}
            sortState={sortState}
            pagination={lossRecoveryData?.overviewTableData?.pagination}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onCustomize={toggleCustomizeDrawer}
            columns={filters["defaultColsOverviewTable"]}
            loading={loading["overviewTableLoading"]}
            onExport={handleOverviewExport}
            selectedAction={selectedAction}
            exportLoading={exportLoading}
            onSelectView={handleViewSelection}
            selectedView={selectedView}
          />
        </div>
      </div>

      {isDrawerOpen.filterDrawer && (
        <MoreFilters
          anchor={"right"}
          filterDrawer={isDrawerOpen.filterDrawer}
          handleFilterDrawer={toggleFilterDrawer}
          rmpmFilters={filters["lossRecoveryFiltersData"]}
          type={"Loss Recovery Engine"}
          setRmpmFilters={(updated) => {
            setFilters((prev) => ({
              ...prev,
              lossRecoveryFiltersData: updated,
            }));

            // Trigger all APIs immediately
            fetchData(updated).catch((error) => {
              if (!isAbortError(error)) {
                console.error("Error fetching data:", error);
                showErrorSnackbar(error);
              }
            });

            // Refresh filter metadata in background
            refreshFilterMeta(updated).catch((error) => {
              if (!isAbortError(error)) {
                console.error("Error refreshing filter meta:", error);
                showErrorSnackbar(error);
              }
            });
          }}
          username={activeAccount?.username}
          limit={9}
        />
      )}

      {isDrawerOpen.customiseDrawer && (
        <CustomiseColumns
          anchor={"right"}
          columns={[...filters["defaultColsOverviewTable"]]}
          isOpen={isDrawerOpen.customiseDrawer}
          handleCustomizeDrawer={toggleCustomizeDrawer}
          columnCustomization={(customizedColumns) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              defaultColsOverviewTable: customizedColumns,
            }));
            toggleCustomizeDrawer();
          }}
          username={activeAccount?.username}
          type="Loss Recovery Engine"
        />
      )}

      {openCalendar && (
        <div className={styles.calendarPopover}>
          <CustomCalendar
            mode="range"
            value={calendarValue}
            onChange={(val) => setCalendarValue(val)}
            onDone={(val) => {
              setCalendarValue(val || { start: null, end: null });
              setOpenCalendar(false);
            }}
            onCancel={() => {
              setOpenCalendar(false);
            }}
            showSlider={true}
          />
        </div>
      )}

      {/* FULL-PAGE EXPORT OVERLAY */}
      {exportLoading && (
        <div className={styles.exportOverlay}>
          <div className={styles.exportLoaderCard}>
            <span className={styles.dot}></span>
            <span className={styles.dot}></span>
            <span className={styles.dot}></span>
          </div>
        </div>
      )}

      <SnackBarMessage
        type={snackbarType}
        snackbarMessage={snackbarMessage}
        snackbarOpen={snackbarOpen}
        setSnackbarOpen={setSnackbarOpen}
      />
    </div>
  );
};

LossRecoveryEngine.propTypes = {
  isActive: PropTypes.bool,
};

export default LossRecoveryEngine;
